/**
 * Small, dependency-free HTML helpers for providers that publish only a rendered page. They are
 * regex- and tokenizer-based on purpose: the same code runs in the browser (no DOMParser in the
 * Worker) and in Node, and a provider page is read for a handful of labelled sections, not
 * validated. Anything they cannot find is `undefined` or empty, never a guess.
 */

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  deg: "°",
  middot: "·",
  ndash: "–",
  mdash: "—",
  lsquo: "‘",
  rsquo: "’",
  laquo: "«",
  raquo: "»",
  hellip: "…",
  euro: "€",
  copy: "©",
  reg: "®",
  aacute: "á",
  agrave: "à",
  acirc: "â",
  auml: "ä",
  eacute: "é",
  egrave: "è",
  ecirc: "ê",
  euml: "ë",
  iacute: "í",
  igrave: "ì",
  icirc: "î",
  iuml: "ï",
  oacute: "ó",
  ograve: "ò",
  ocirc: "ô",
  ouml: "ö",
  uacute: "ú",
  ugrave: "ù",
  ucirc: "û",
  uuml: "ü",
  ntilde: "ñ",
  ccedil: "ç",
  szlig: "ß",
};

/** Decodes numeric and the common named entities; an unknown name is left as written. */
export function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi, (whole, name: string) => {
    if (name.startsWith("#")) {
      const code =
        name[1]?.toLowerCase() === "x" ? parseInt(name.slice(2), 16) : Number(name.slice(1));
      return Number.isInteger(code) && code > 0 && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : whole;
    }
    return NAMED_ENTITIES[name.toLowerCase()] ?? whole;
  });
}

/** Plain text from a fragment; block ends become spaces so words never fuse. */
export function stripHtml(html: string): string {
  return decodeEntities(
    html
      .replace(/<\s*(?:br|\/p|\/li|\/div|\/h[1-6]|\/t[dh]|\/tr)\b[^>]*>/gi, " ")
      .replace(/<[^>]*>/g, ""),
  )
    .replace(/\s+/g, " ")
    .trim();
}

/** Removes comments (inert templates a page may fill for other products), scripts and styles. */
export function stripNonContent(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<(script|style|noscript|template)\b[\s\S]*?<\/\1\s*>/gi, "");
}

export interface HtmlElement {
  /** The opening tag as written, attributes included. */
  readonly open: string;
  /** Everything between the opening and the matching closing tag. */
  readonly inner: string;
  /** Offset of the opening tag in the source. */
  readonly start: number;
  /** Offset just after the closing tag. */
  readonly end: number;
}

const VOID_TAGS: ReadonlySet<string> = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "source",
  "track",
  "wbr",
]);

/**
 * Top-level elements of `tag` (case-insensitive) whose opening tag passes `where`, with the
 * closing tag matched by nesting depth. An element never closed runs to the end of the input.
 * Elements nested inside a returned element are not returned again.
 */
export function elements(
  html: string,
  tag: string,
  where: (open: string) => boolean = () => true,
): HtmlElement[] {
  const wanted = tag.toLowerCase();
  const out: HtmlElement[] = [];
  const token = /<(\/?)([a-z][a-z0-9-]*)\b[^>]*?(\/?)>/gi;
  let current: { open: string; start: number; innerStart: number; depth: number } | undefined;
  for (let m = token.exec(html); m !== null; m = token.exec(html)) {
    const name = (m[2] ?? "").toLowerCase();
    if (name !== wanted) continue;
    const closing = m[1] === "/";
    const selfClosing = m[3] === "/" || VOID_TAGS.has(name);
    if (current === undefined) {
      if (closing || !where(m[0])) continue;
      if (selfClosing) {
        out.push({ open: m[0], inner: "", start: m.index, end: m.index + m[0].length });
        continue;
      }
      current = { open: m[0], start: m.index, innerStart: m.index + m[0].length, depth: 1 };
    } else if (closing) {
      current.depth -= 1;
      if (current.depth === 0) {
        out.push({
          open: current.open,
          inner: html.slice(current.innerStart, m.index),
          start: current.start,
          end: m.index + m[0].length,
        });
        current = undefined;
      }
    } else if (!selfClosing) {
      current.depth += 1;
    }
  }
  if (current !== undefined) {
    out.push({
      open: current.open,
      inner: html.slice(current.innerStart),
      start: current.start,
      end: html.length,
    });
  }
  return out;
}

/** The value of an attribute in an opening tag, entity-decoded, or `undefined`. */
export function attribute(open: string, name: string): string | undefined {
  // One literal pattern walks every attribute, so the name never reaches a RegExp constructor.
  const wanted = name.toLowerCase();
  const pairs = /\s([^\s=>/]+)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/g;
  let m: RegExpExecArray | null = pairs.exec(open);
  while (m !== null && m[1]?.toLowerCase() !== wanted) m = pairs.exec(open);
  const value = m?.[2] ?? m?.[3] ?? m?.[4];
  return value === undefined ? undefined : decodeEntities(value);
}

/** True when the opening tag carries the CSS class (a whole word in its class attribute). */
export function hasClass(open: string, cls: string): boolean {
  return (attribute(open, "class") ?? "").split(/\s+/).includes(cls);
}

/**
 * The markup after the first heading (h1-h6) whose text matches, up to the next heading of the same
 * or a higher rank. Section labels are the part of a provider page that stays put across redesigns,
 * so a parser reads "what follows the Ingredients heading" rather than a CSS path.
 */
export function sectionAfterHeading(html: string, heading: RegExp | string): string | undefined {
  const headings = /<h([1-6])\b[^>]*>([\s\S]*?)<\/h\1\s*>/gi;
  let start: { level: number; end: number } | undefined;
  for (let m = headings.exec(html); m !== null; m = headings.exec(html)) {
    const level = Number(m[1]);
    if (start === undefined) {
      const text = stripHtml(m[2] ?? "");
      const hit =
        typeof heading === "string"
          ? text.toLowerCase() === heading.toLowerCase()
          : heading.test(text);
      if (hit) start = { level, end: m.index + m[0].length };
    } else if (level <= start.level) {
      return html.slice(start.end, m.index);
    }
  }
  return start === undefined ? undefined : html.slice(start.end);
}

/**
 * The first number in `text`, or `null`. A comma is a decimal comma ("1,5 g" is 1.5); when both
 * a dot and a comma occur the last one is the decimal mark and the other a thousands separator.
 * A leading "<" ("< 0,5 g") is ignored: the figure is what the producer printed.
 */
export function parseLocalNumber(text: string): number | null {
  const m = /-?\d[\d.,]*/.exec(text);
  if (m === null) return null;
  let raw = m[0].replace(/[.,]+$/, "");
  const lastDot = raw.lastIndexOf(".");
  const lastComma = raw.lastIndexOf(",");
  if (lastDot >= 0 && lastComma >= 0) {
    const decimal = lastDot > lastComma ? "." : ",";
    const thousands = decimal === "." ? "," : ".";
    raw = raw.split(thousands).join("").replace(decimal, ".");
  } else if (lastComma >= 0) {
    raw = raw.replace(",", ".");
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/** Every number in `text`, in order ("336 kJ / 81 kcal" gives [336, 81]). */
export function numbersIn(text: string): number[] {
  const out: number[] = [];
  for (const m of text.matchAll(/-?\d[\d.,]*/g)) {
    const value = parseLocalNumber(m[0]);
    if (value !== null) out.push(value);
  }
  return out;
}

/** A comma-separated list as trimmed, non-empty, de-duplicated items. */
export function splitList(text: string, separator: RegExp = /,/): string[] {
  return [
    ...new Set(
      text
        .split(separator)
        .map((item) => item.replace(/\s+/g, " ").trim())
        .filter((item) => item !== ""),
    ),
  ];
}
